import { useState, useEffect, lazy, Suspense } from "react";
import Header from "@/components/Header";
import HeroSection from "@/components/HeroSection";
import AboutSection from "@/components/AboutSection";
import AnimatedSection from "@/components/AnimatedSection";
import PageTransition from "@/components/PageTransition";

// Lazy load below-fold components to reduce initial bundle size
const TestimonialsTeaser = lazy(() => import("@/components/TestimonialsTeaser"));
const PortfolioSection = lazy(() => import("@/components/PortfolioSection"));
const ServicesSection = lazy(() => import("@/components/ServicesSection"));
const TestimonialsSection = lazy(() => import("@/components/TestimonialsSection"));
const ConsultationSection = lazy(() => import("@/components/ConsultationSection"));
const ContactSection = lazy(() => import("@/components/ContactSection"));
const MouseFollowBackground = lazy(() => import("@/components/MouseFollowBackground"));
const BackToTop = lazy(() => import("@/components/BackToTop"));
const FloatingTelegramButton = lazy(() => import("@/components/FloatingTelegramButton"));
const StickyCTABar = lazy(() => import("@/components/StickyCTABar"));
const ScrollEndPopup = lazy(() => import("@/components/ScrollEndPopup"));

const sectionIds = ["about", "consultation", "portfolio", "services", "testimonials", "contact"];

const Index = () => {
  const [activeSection, setActiveSection] = useState("about");

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: "-20% 0px -60% 0px", // Trigger when section is in upper-middle of viewport
      threshold: 0,
    };

    const observerCallback: IntersectionObserverCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    sectionIds.forEach((id) => {
      const element = document.getElementById(id);
      if (element) {
        observer.observe(element);
      }
    });

    return () => observer.disconnect();
  }, []);

  const handleNavigate = (section: string) => {
    setActiveSection(section);
    const element = document.getElementById(section);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <PageTransition>
      <div className="min-h-screen">
        <Suspense fallback={null}>
          <MouseFollowBackground />
        </Suspense>
        <Header activeSection={activeSection} onNavigate={handleNavigate} />
        
        {/* Main Content */}
        <main className="min-h-screen pt-20">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 md:px-8 lg:px-12">
            <AnimatedSection>
              <HeroSection />
            </AnimatedSection>
            <Suspense fallback={<div className="min-h-[80px]" />}>
              <TestimonialsTeaser />
            </Suspense>
            <AnimatedSection delay={0.1}>
              <AboutSection />
            </AnimatedSection>
            <Suspense fallback={<div className="min-h-[200px]" />}>
              <AnimatedSection>
                <ConsultationSection />
              </AnimatedSection>
              <AnimatedSection>
                <PortfolioSection />
              </AnimatedSection>
              <AnimatedSection>
                <ServicesSection />
              </AnimatedSection>
              <AnimatedSection>
                <TestimonialsSection />
              </AnimatedSection>
              <AnimatedSection>
                <ContactSection />
              </AnimatedSection>
            </Suspense>
          </div>
        </main>
        
        {/* Footer */}
        <footer className="mt-16 py-8 text-center text-sm text-muted-foreground border-t border-border/50 bg-muted/30 backdrop-blur-sm">
          <div className="max-w-5xl mx-auto px-4">
            2026 powered by Any
          </div>
        </footer>
        
        <Suspense fallback={null}>
          <BackToTop />
          <FloatingTelegramButton />
          <StickyCTABar />
          <ScrollEndPopup />
        </Suspense>
      </div>
    </PageTransition>
  );
};

export default Index;
